import React, { useState } from 'react';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import type { CreatePersonaPayload, RorosPersonaDTO } from '@/types/roros/overarching';

/**
 * PersonaFormBody — headless form body (NO Modal wrapper) that renders
 * the full 10-field persona form with the canonical 2-column layout for
 * Name/Role and Decision role/Influence level, then textareas stacked.
 *
 * Used by:
 *   - `AddPersonaModal` (inside `MarketTree.tsx`) — already inside a
 *     Modal for the Pick-vs-Create tabbed flow, so it embeds this body
 *     directly without another Modal wrapper. (Uncontrolled mode.)
 *   - `PersonaFormDialog` — thin FormSheet wrapper around this body for the
 *     standalone create/edit flows (MarketTree Pencil, PersonasPage,
 *     PersonaDetailPage). (Controlled mode: Sheet Wave 3b.)
 *
 * This is the single source of truth for persona form layout and
 * submit logic. All persona surfaces use this component directly.
 *
 * Controlled mode (Wave 3b FormSheet migration):
 *   When `value` and `onChange` are both provided, PersonaFormBody uses
 *   those for rendering and notifying changes, and omits the Cancel/Save
 *   footer buttons (FormSheet's header provides them). This preserves the
 *   AddPersonaModal usage unchanged (uncontrolled mode).
 */

type PersonaDraft = CreatePersonaPayload;

/** All 10 persona form fields as plain strings (empty string = null/none). */
export type PersonaFormValues = {
  name: string;
  role: string;
  decisionRole: string;
  influenceLevel: string;
  demographics: string;
  communicationStyle: string;
  needs: string;
  goals: string;
  painPoints: string;
  objections: string;
};

interface PersonaFormBodyProps {
  // ── Uncontrolled mode (AddPersonaModal usage — backward compatible) ─────
  /** Seed values for edit. Undefined = empty form for create. */
  initialValues?: RorosPersonaDTO | null;
  /** Called with the full 10-field draft when the user submits. Required in uncontrolled mode. */
  onSubmit?: (draft: PersonaDraft) => Promise<void> | void;
  onCancel?: () => void;
  /** Button label — e.g. "Create & Link", "Create", "Save Changes". */
  submitLabel?: string;
  /** External submitting state (mutation in flight). */
  isSubmitting?: boolean;

  // ── Controlled mode (PersonaFormDialog / FormSheet usage — Wave 3b) ────
  /**
   * When provided, PersonaFormBody uses these values for rendering instead
   * of its own internal state. Must be paired with `onChange`.
   */
  value?: PersonaFormValues;
  /**
   * When provided, PersonaFormBody calls this setter on every field change
   * instead of its own internal setState. Must be paired with `value`.
   */
  onChange?: (k: keyof PersonaFormValues, v: string) => void;
}

function toFormValue(v: string | null | undefined): string {
  return v ?? '';
}

function cleaned(s: string): string | null {
  const trimmed = s.trim();
  return trimmed === '' ? null : trimmed;
}

export function PersonaFormBody({
  initialValues,
  onSubmit,
  onCancel,
  submitLabel,
  isSubmitting = false,
  value: controlledValue,
  onChange: controlledOnChange,
}: PersonaFormBodyProps) {
  // Internal state used only in uncontrolled mode (AddPersonaModal usage).
  const [internalForm, setInternalForm] = useState({
    name: initialValues?.name ?? '',
    role: toFormValue(initialValues?.role),
    decisionRole: toFormValue(initialValues?.decisionRole),
    influenceLevel: toFormValue(initialValues?.influenceLevel),
    demographics: toFormValue(initialValues?.demographics),
    needs: toFormValue(initialValues?.needs),
    goals: toFormValue(initialValues?.goals),
    painPoints: toFormValue(initialValues?.painPoints),
    objections: toFormValue(initialValues?.objections),
    communicationStyle: toFormValue(initialValues?.communicationStyle),
  });
  const [localBusy, setLocalBusy] = useState(false);

  // Detect which mode we're in.
  const isControlled = controlledValue !== undefined && controlledOnChange !== undefined;

  // Active form values: controlled or internal.
  const form = isControlled ? controlledValue! : internalForm;

  // Field setter: routes to controlled onChange or internal setState.
  const set = (k: keyof PersonaFormValues, v: string) => {
    if (isControlled) {
      controlledOnChange!(k, v);
    } else {
      setInternalForm((prev) => ({ ...prev, [k]: v }));
    }
  };

  const busy = isSubmitting || localBusy;

  // Uncontrolled submit handler (only used when not in controlled mode).
  async function handleSubmit() {
    if (!form.name.trim()) {
      showToast.error(t('Name is required'));
      return;
    }
    const draft: PersonaDraft = {
      name: form.name.trim(),
      role: cleaned(form.role),
      decisionRole: (form.decisionRole || null) as PersonaDraft['decisionRole'],
      influenceLevel: (form.influenceLevel || null) as PersonaDraft['influenceLevel'],
      demographics: cleaned(form.demographics),
      needs: cleaned(form.needs),
      goals: cleaned(form.goals),
      painPoints: cleaned(form.painPoints),
      objections: cleaned(form.objections),
      communicationStyle: cleaned(form.communicationStyle),
    };
    setLocalBusy(true);
    try {
      await onSubmit!(draft);
    } finally {
      setLocalBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          label={t('Name')}
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder={t('e.g. Marketing Manager Maria')}
          required
        />
        <Input
          label={t('Role')}
          value={form.role}
          onChange={(e) => set('role', e.target.value)}
          placeholder={t('e.g. VP Engineering')}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select
          label={t('Decision role')}
          value={form.decisionRole}
          onValueChange={(v) => set('decisionRole', v)}
          placeholder={t('— Select —')}
          options={[
            { value: 'decider', label: t('Decider') },
            { value: 'influencer', label: t('Influencer') },
            { value: 'user', label: t('User') },
            { value: 'blocker', label: t('Blocker') },
            { value: 'champion', label: t('Champion') },
          ]}
        />
        <Select
          label={t('Influence level')}
          value={form.influenceLevel}
          onValueChange={(v) => set('influenceLevel', v)}
          placeholder={t('— Select —')}
          options={[
            { value: 'low', label: t('Low') },
            { value: 'medium', label: t('Medium') },
            { value: 'high', label: t('High') },
          ]}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Textarea
          label={t('Demographics')}
          value={form.demographics}
          onChange={(e) => set('demographics', e.target.value)}
          placeholder={t('Age range, job title, industry, company size...')}
          rows={4}
        />
        <Textarea
          label={t('Communication style')}
          value={form.communicationStyle}
          onChange={(e) => set('communicationStyle', e.target.value)}
          placeholder={t('How do they prefer to communicate?')}
          rows={4}
        />
        <Textarea
          label={t('Needs')}
          value={form.needs}
          onChange={(e) => set('needs', e.target.value)}
          placeholder={t('What does this persona need?')}
          rows={4}
        />
        <Textarea
          label={t('Goals')}
          value={form.goals}
          onChange={(e) => set('goals', e.target.value)}
          placeholder={t('What does this persona want to achieve?')}
          rows={4}
        />
        <Textarea
          label={t('Pain points')}
          value={form.painPoints}
          onChange={(e) => set('painPoints', e.target.value)}
          placeholder={t('What frustrations do they have today?')}
          rows={4}
        />
        <Textarea
          label={t('Objections')}
          value={form.objections}
          onChange={(e) => set('objections', e.target.value)}
          placeholder={t('What might stop them from buying?')}
          rows={4}
        />
      </div>

      {/* Footer buttons — only rendered in uncontrolled mode.
          In controlled mode (FormSheet usage), FormSheet's header provides
          Cancel and Save. */}
      {!isControlled && (
        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            {t('Cancel')}
          </Button>
          <Button type="button" onClick={handleSubmit} isLoading={busy}>
            {submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
